import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, AlertTriangle, CheckCircle, Download, ArrowRight, Shield, X, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import jsPDF from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══ Industry Models ═══ */
const INDUSTRIES = {
  casinos: {
    labelKey: 'casinos',
    fields: [
      { key: 'gamingSections', labelKey: 'gamingSections', min: 7, max: 8, typical: [1, 20] },
      { key: 'bars', labelKey: 'bars', min: 5, max: 6, typical: [1, 12] },
      { key: 'restaurants', labelKey: 'restaurants', min: 5, max: 6, typical: [1, 10] },
      { key: 'entrances', labelKey: 'entrances', min: 5, max: 6, typical: [2, 12] },
      { key: 'elevators', labelKey: 'elevators', min: 3, max: 4, typical: [1, 15] },
      { key: 'escalators', labelKey: 'escalators', min: 3, max: 4, typical: [0, 12] },
      { key: 'washrooms', labelKey: 'washrooms', min: 4, max: 5, typical: [2, 20] },
      { key: 'parkingEntrances', labelKey: 'parkingEntrances', min: 5, max: 6, typical: [1, 8] },
      { key: 'loadingDocks', labelKey: 'loadingDocks', min: 6, max: 7, typical: [1, 6] },
      { key: 'cashierCages', labelKey: 'cashierCages', min: 5, max: 6, typical: [1, 10] },
      { key: 'securityCheckpoints', labelKey: 'securityCheckpoints', min: 7, max: 8, typical: [1, 10] },
    ],
  },
  hotels: {
    labelKey: 'hotels',
    fields: [
      { key: 'floors', labelKey: 'floors', min: 2, max: 3, typical: [2, 50] },
      { key: 'elevators', labelKey: 'elevators', min: 2, max: 3, typical: [1, 20] },
      { key: 'entrances', labelKey: 'entrances', min: 4, max: 5, typical: [1, 12] },
      { key: 'washrooms', labelKey: 'washrooms', min: 3, max: 4, typical: [2, 30] },
      { key: 'restaurants', labelKey: 'restaurants', min: 5, max: 6, typical: [0, 10] },
      { key: 'bars', labelKey: 'bars', min: 4, max: 5, typical: [0, 8] },
      { key: 'pools', labelKey: 'pools', min: 7, max: 8, typical: [0, 5] },
      { key: 'gyms', labelKey: 'gyms', min: 4, max: 5, typical: [0, 4] },
      { key: 'spas', labelKey: 'spas', min: 5, max: 6, typical: [0, 3] },
      { key: 'conferenceRooms', labelKey: 'conferenceRooms', min: 2, max: 3, typical: [0, 20] },
      { key: 'banquetHalls', labelKey: 'banquetHalls', min: 5, max: 6, typical: [0, 6] },
      { key: 'loadingDocks', labelKey: 'loadingDocks', min: 5, max: 6, typical: [1, 6] },
      { key: 'laundryAreas', labelKey: 'laundryAreas', min: 3, max: 4, typical: [1, 4] },
      { key: 'maintenanceRooms', labelKey: 'maintenanceRooms', min: 2, max: 3, typical: [1, 6] },
    ],
  },
  retail: {
    labelKey: 'retail',
    fields: [
      { key: 'aisles', labelKey: 'aisles', min: 3, max: 4, typical: [5, 60] },
      { key: 'entrances', labelKey: 'entrances', min: 4, max: 5, typical: [1, 8] },
      { key: 'checkoutLanes', labelKey: 'checkoutLanes', min: 3, max: 4, typical: [2, 40] },
      { key: 'refrigerationAreas', labelKey: 'refrigerationAreas', min: 4, max: 5, typical: [0, 10] },
      { key: 'washrooms', labelKey: 'washrooms', min: 3, max: 4, typical: [1, 8] },
      { key: 'loadingDocks', labelKey: 'loadingDocks', min: 5, max: 6, typical: [1, 8] },
      { key: 'serviceDepartments', labelKey: 'serviceDepartments', min: 4, max: 5, typical: [0, 12] },
      { key: 'storageZones', labelKey: 'storageZones', min: 2, max: 3, typical: [1, 10] },
    ],
  },
  airports: {
    labelKey: 'airports',
    fields: [
      { key: 'terminals', labelKey: 'terminals', min: 10, max: 12, typical: [1, 8] },
      { key: 'gates', labelKey: 'gates', min: 1, max: 2, typical: [5, 200] },
      { key: 'securityCheckpoints', labelKey: 'securityCheckpoints', min: 7, max: 8, typical: [1, 20] },
      { key: 'washrooms', labelKey: 'washrooms', min: 4, max: 5, typical: [5, 80] },
      { key: 'baggageClaim', labelKey: 'baggageClaim', min: 5, max: 6, typical: [2, 30] },
      { key: 'elevators', labelKey: 'elevators', min: 3, max: 4, typical: [2, 30] },
      { key: 'escalators', labelKey: 'escalators', min: 3, max: 4, typical: [2, 30] },
      { key: 'boardingAreas', labelKey: 'boardingAreas', min: 4, max: 5, typical: [2, 50] },
      { key: 'customsCheckpoints', labelKey: 'customsCheckpoints', min: 7, max: 8, typical: [0, 10] },
    ],
  },
  hospitals: {
    labelKey: 'hospitals',
    fields: [
      { key: 'patientUnits', labelKey: 'patientUnits', min: 5, max: 6, typical: [2, 30] },
      { key: 'operatingRooms', labelKey: 'operatingRooms', min: 5, max: 6, typical: [2, 20] },
      { key: 'emergencyDepts', labelKey: 'emergencyDepts', min: 7, max: 8, typical: [1, 4] },
      { key: 'elevators', labelKey: 'elevators', min: 3, max: 4, typical: [2, 20] },
      { key: 'entrances', labelKey: 'entrances', min: 4, max: 5, typical: [2, 10] },
      { key: 'washrooms', labelKey: 'washrooms', min: 4, max: 5, typical: [5, 40] },
      { key: 'waitingAreas', labelKey: 'waitingAreas', min: 3, max: 4, typical: [2, 15] },
      { key: 'laboratories', labelKey: 'laboratories', min: 5, max: 6, typical: [1, 10] },
      { key: 'loadingDocks', labelKey: 'loadingDocks', min: 6, max: 7, typical: [1, 5] },
      { key: 'icuUnits', labelKey: 'icuUnits', min: 6, max: 7, typical: [1, 8] },
    ],
  },
  stadiums: {
    labelKey: 'stadiums',
    fields: [
      { key: 'seatingSections', labelKey: 'seatingSections', min: 5, max: 6, typical: [4, 60] },
      { key: 'entrances', labelKey: 'entrances', min: 5, max: 6, typical: [2, 20] },
      { key: 'concessionAreas', labelKey: 'concessionAreas', min: 4, max: 5, typical: [4, 40] },
      { key: 'washrooms', labelKey: 'washrooms', min: 5, max: 6, typical: [4, 40] },
      { key: 'elevators', labelKey: 'elevators', min: 3, max: 4, typical: [1, 12] },
      { key: 'escalators', labelKey: 'escalators', min: 3, max: 4, typical: [1, 12] },
      { key: 'lockerRooms', labelKey: 'lockerRooms', min: 4, max: 5, typical: [1, 6] },
      { key: 'parkingEntrances', labelKey: 'parkingEntrances', min: 5, max: 6, typical: [2, 12] },
    ],
  },
};

const COVERAGE_KEYS = [
  { key: 'low', labelKey: 'coverageLow', multiplier: 0.5 },
  { key: 'standard', labelKey: 'coverageStandard', multiplier: 1.0 },
  { key: 'max', labelKey: 'coverageMax', multiplier: 1.5 },
];

const MIN_THRESHOLDS = { starter: 20, small: 40, medium: 75, large: 120 };

const getTierKey = (tags) => {
  if (tags >= 500) return { key: 'enterprise', color: 'text-red-600 bg-red-50 border-red-200' };
  if (tags >= 200) return { key: 'large', color: 'text-amber-700 bg-amber-50 border-amber-200' };
  if (tags >= 100) return { key: 'medium', color: 'text-sky-700 bg-sky-50 border-sky-200' };
  if (tags >= 50) return { key: 'small', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
  return { key: 'starter', color: 'text-slate-600 bg-slate-50 border-slate-200' };
};

const getMinThreshold = (tags) => {
  if (tags >= 200) return MIN_THRESHOLDS.large;
  if (tags >= 100) return MIN_THRESHOLDS.medium;
  if (tags >= 50) return MIN_THRESHOLDS.small;
  return MIN_THRESHOLDS.starter;
};

/* ═══ Review Modal ═══ */
const ReviewModal = ({ onClose, onSubmit, submitting, td }) => {
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [honeypot, setHoneypot] = useState('');

  useEffect(() => {
    firstInputRef.current?.focus();
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleTab = useCallback((e) => {
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusable = modalRef.current.querySelectorAll('input,textarea,button,[tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, []);

  const valid = form.name.trim() && form.email.trim() && form.company.trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={td('review.modalTitle')}
      data-testid="review-modal-overlay"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto p-6 md:p-8 shadow-2xl relative"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleTab}
        data-testid="review-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
          data-testid="review-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-bold text-slate-900 mb-1">{td('review.modalTitle')}</h3>
        <p className="text-sm text-slate-500 mb-6">{td('review.modalSubtitle')}</p>

        <form onSubmit={e => { e.preventDefault(); if (valid) onSubmit({ ...form, honeypot }); }}>
          <input type="text" name="company_url" value={honeypot} onChange={e => setHoneypot(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{td('review.nameLabel')} *</label>
              <input
                ref={firstInputRef}
                type="text"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                required
                data-testid="review-input-name"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{td('review.emailLabel')} *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                required
                data-testid="review-input-email"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{td('review.companyLabel')} *</label>
              <input
                type="text"
                value={form.company}
                onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                required
                data-testid="review-input-company"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{td('review.phoneLabel')}</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="review-input-phone"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{td('review.messageLabel')}</label>
              <textarea
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                data-testid="review-input-message"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={!valid || submitting}
            className="w-full mt-6 px-5 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            data-testid="review-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {td('review.submitting')}</>
            ) : (
              td('review.submitBtn')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ═══ Main Component ═══ */
const DeploymentEstimator = () => {
  const { t, language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  const [industry, setIndustry] = useState('');
  const [coverage, setCoverage] = useState('standard');
  const [inputs, setInputs] = useState({});
  const [warnings, setWarnings] = useState({});

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewError, setReviewError] = useState(false);

  const model = INDUSTRIES[industry];
  const coverageMultiplier = COVERAGE_KEYS.find(c => c.key === coverage)?.multiplier || 1.0;

  const tf = (key) => t(`operationalRisk.deploymentEstimator.fields.${key}`);
  const ti = (key) => t(`operationalRisk.deploymentEstimator.industries.${key}`);
  const tt = (key) => t(`operationalRisk.deploymentEstimator.tiers.${key}`);
  const td = (key) => t(`operationalRisk.deploymentEstimator.${key}`);

  const handleIndustryChange = (key) => {
    setIndustry(key);
    setInputs({});
    setWarnings({});
    setReviewSubmitted(false);
    setReviewError(false);
  };

  const handleInput = (key, value, field) => {
    const num = value === '' ? '' : parseInt(value) || 0;
    setInputs(prev => ({ ...prev, [key]: num }));
    if (num !== '' && field.typical && (num < field.typical[0] || num > field.typical[1])) {
      setWarnings(prev => ({ ...prev, [key]: true }));
    } else {
      setWarnings(prev => ({ ...prev, [key]: false }));
    }
  };

  const results = useMemo(() => {
    if (!model) return null;
    const hasInput = Object.values(inputs).some(v => v !== '' && v > 0);
    if (!hasInput) return null;

    let totalMin = 0;
    let totalMax = 0;
    let coverageAreas = 0;

    model.fields.forEach(field => {
      const count = parseInt(inputs[field.key]) || 0;
      if (count > 0) {
        totalMin += count * field.min;
        totalMax += count * field.max;
        coverageAreas += count;
      }
    });

    totalMin = Math.round(totalMin * coverageMultiplier);
    totalMax = Math.round(totalMax * coverageMultiplier);

    const minThreshold = getMinThreshold(totalMax);
    if (totalMin < minThreshold) totalMin = minThreshold;
    if (totalMax < minThreshold) totalMax = minThreshold;

    const tier = getTierKey(Math.round((totalMin + totalMax) / 2));

    return { totalMin, totalMax, coverageAreas, tier };
  }, [model, inputs, coverageMultiplier]);

  const handleReviewSubmit = async (formData) => {
    setReviewSubmitting(true);
    setReviewError(false);

    const components = {};
    if (model) {
      model.fields.forEach(f => {
        const v = parseInt(inputs[f.key]) || 0;
        if (v > 0) components[f.key] = v;
      });
    }

    const payload = {
      contact: {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone || '',
        message: formData.message || '',
      },
      business: { industry },
      deployment: {
        components,
        coverageIntensity: coverage,
        minTags: results?.totalMin || 0,
        maxTags: results?.totalMax || 0,
        tier: results?.tier?.key || '',
      },
      exposure: null,
      metadata: {
        language,
        timestamp: new Date().toISOString(),
        source: 'Deployment Estimator',
      },
      honeypot: formData.honeypot || '',
    };

    try {
      const res = await fetch(`${API_URL}/api/contact/request-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed');
      setReviewSubmitted(true);
      setShowReviewModal(false);
    } catch {
      setReviewError(true);
    } finally {
      setReviewSubmitting(false);
    }
  };

  const exportPDF = () => {
    if (!results || !model) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    let y = 25;
    doc.setFontSize(18); doc.setTextColor(15, 23, 42);
    doc.text(td('pdfTitle'), pw / 2, y, { align: 'center' }); y += 8;
    doc.setFontSize(10); doc.setTextColor(100, 116, 139);
    doc.text(ti(model.labelKey) + ' | ' + td(COVERAGE_KEYS.find(c => c.key === coverage)?.labelKey), pw / 2, y, { align: 'center' }); y += 12;
    doc.setDrawColor(226, 232, 240); doc.line(20, y, pw - 20, y); y += 10;
    doc.setFontSize(12); doc.setTextColor(15, 23, 42); doc.text(td('pdfComponentInputs'), 20, y); y += 8;
    doc.setFontSize(9); doc.setTextColor(71, 85, 105);
    model.fields.forEach(f => {
      const v = inputs[f.key] || 0;
      if (v > 0) { doc.text(`${tf(f.labelKey)}: ${v}`, 24, y); y += 6; }
    });
    y += 6; doc.line(20, y, pw - 20, y); y += 10;
    doc.setFontSize(12); doc.setTextColor(15, 23, 42); doc.text(td('pdfEstimatedDeployment'), 20, y); y += 8;
    doc.setFontSize(10); doc.setTextColor(71, 85, 105);
    doc.text(`${td('estimatedTagRange')}: ${results.totalMin} – ${results.totalMax}`, 24, y); y += 7;
    doc.text(`${td('coverageAreas')}: ${results.coverageAreas}`, 24, y); y += 7;
    doc.text(`${td('deploymentTier')}: ${tt(results.tier.key)}`, 24, y); y += 14;
    doc.setDrawColor(226, 232, 240); doc.line(20, y, pw - 20, y); y += 10;
    doc.setFontSize(8); doc.setTextColor(140, 140, 140);
    const disclaimer = td('pdfDisclaimer');
    const lines = doc.splitTextToSize(disclaimer, pw - 40);
    doc.text(lines, 20, y);
    doc.save('tapelligence-deployment-estimate.pdf');
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="deployment-estimator">
      {/* Risk Statement */}
      <div className="mb-8 text-center" data-testid="risk-statement">
        <h3 className="text-lg font-bold text-slate-900 mb-2">{td('riskStatementTitle')}</h3>
        <p className="text-sm text-slate-600 leading-relaxed max-w-2xl mx-auto">{td('riskStatementBody')}</p>
      </div>

      <div className="text-center mb-10">
        <span className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3 block">{td('label')}</span>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="estimator-title">{td('title')}</h2>
        <p className="text-base text-slate-600 mb-2">{td('subtitle')}</p>
        <p className="text-xs text-slate-400 italic max-w-2xl mx-auto">{td('disclaimerSubtitle')}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
        <div className="bg-blue-600 px-8 py-5 flex items-center gap-3 rounded-t-2xl" data-testid="risk-model-header-bar">
          <Shield className="w-5 h-5 text-white" />
          <h3 className="text-white font-semibold">{td('title')}</h3>
        </div>
        <div className="p-6 md:p-8">
        {/* Industry Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">{td('selectIndustry')}</label>
          <div className="relative">
            <select
              value={industry}
              onChange={e => handleIndustryChange(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 appearance-none"
              data-testid="estimator-industry-select"
            >
              <option value="">{td('industryPlaceholder')}</option>
              {Object.entries(INDUSTRIES).map(([key, val]) => (
                <option key={key} value={key}>{ti(val.labelKey)}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          {industry === 'casinos' && (
            <div className="mt-3" data-testid="casino-helper">
              <p className="text-sm text-slate-600 leading-relaxed">{td('casinoHelper')}</p>
              <p className="text-xs text-slate-400 mt-1 italic">{td('casinoHelperSecondary')}</p>
            </div>
          )}
        </div>

        {/* Coverage Intensity */}
        {model && (
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-700 mb-2">{td('coverageIntensity')}</label>
            <div className="grid grid-cols-3 gap-2" data-testid="coverage-toggle">
              {COVERAGE_KEYS.map(level => (
                <button
                  key={level.key}
                  onClick={() => setCoverage(level.key)}
                  className={`py-2.5 px-3 rounded-lg text-xs font-semibold transition-all border ${
                    coverage === level.key
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`coverage-${level.key}`}
                >
                  {td(level.labelKey)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Component Inputs */}
        {model && (
          <div className="mb-8">
            <p className="text-sm font-medium text-slate-700 mb-4">{td('operationalComponents')}</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4" data-testid="component-inputs">
              {model.fields.map(field => (
                <div key={field.key}>
                  <label className="block text-xs text-slate-500 mb-1">{tf(field.labelKey)}</label>
                  <input
                    type="number"
                    min="0"
                    value={inputs[field.key] ?? ''}
                    onChange={e => handleInput(field.key, e.target.value, field)}
                    placeholder="0"
                    className={`w-full px-3 py-2.5 bg-slate-50 border rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 ${
                      warnings[field.key] ? 'border-amber-400' : 'border-slate-200'
                    }`}
                    data-testid={`input-${field.key}`}
                  />
                  {warnings[field.key] && (
                    <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                      {td('outsideRange')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Casino Risk Zones */}
        {industry === 'casinos' && model && (
          <div className="mb-8" data-testid="casino-risk-zones">
            <p className="text-sm font-medium text-slate-700 mb-3">{td('casinoRiskZonesTitle')}</p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {(td('casinoRiskZones') || []).map((zone, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="w-1 h-1 bg-red-400 rounded-full flex-shrink-0" />
                  {zone}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="border-t border-slate-200 pt-8" data-testid="estimator-results">
            <div className="grid md:grid-cols-3 gap-5 mb-6">
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 text-center">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">{td('estimatedTagRange')}</p>
                <p className="font-bold text-slate-900 font-mono leading-tight" style={{ fontSize: `${Math.max(16, Math.min(28, 28 - Math.max(0, String(results.totalMin + ' – ' + results.totalMax).length - 8) * 1.5))}px` }} data-testid="result-tag-range">
                  {results.totalMin} – {results.totalMax}
                </p>
              </div>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 text-center">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">{td('coverageAreas')}</p>
                <p className="font-bold text-slate-900 font-mono leading-tight" style={{ fontSize: `${Math.max(16, Math.min(28, 28 - Math.max(0, String(results.coverageAreas).length - 6) * 2))}px` }} data-testid="result-coverage-areas">
                  {results.coverageAreas}
                </p>
              </div>
              <div className={`rounded-xl p-5 border text-center ${results.tier.color}`}>
                <p className="text-xs uppercase tracking-wider mb-2 font-medium">{td('deploymentTier')}</p>
                <p className="text-2xl md:text-3xl font-bold font-mono" data-testid="result-tier">
                  {tt(results.tier.key)}
                </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 mb-6" data-testid="estimator-disclaimer">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-600 leading-relaxed">{td('disclaimerBody')}</p>
                  <p className="text-xs text-slate-500 leading-relaxed mt-2">{td('disclaimerTailored')}</p>
                </div>
              </div>
            </div>

            {/* Export + CTA */}
            <div className="flex flex-wrap items-center gap-4 justify-center mb-10">
              <button
                onClick={exportPDF}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl transition-colors"
                data-testid="estimator-export-btn"
              >
                <Download className="w-4 h-4" />
                {td('exportBtn')}
              </button>
              <Link
                to={getLocalizedPath('/contact')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors"
                data-testid="estimator-demo-cta"
              >
                {td('requestDemo')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* ═══ Coverage Review Section ═══ */}
            <div className="border-t border-slate-200 pt-8" data-testid="coverage-review-section">
              {reviewSubmitted ? (
                <div className="text-center py-6" data-testid="review-confirmation">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald-50 rounded-full mb-4">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900 mb-1">{td('review.confirmationTitle')}</p>
                  <p className="text-sm text-slate-500">{td('review.confirmationBody')}</p>
                </div>
              ) : (
                <div className="text-center">
                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-testid="review-heading">{td('review.heading')}</h3>
                  <p className="text-sm text-slate-500 mb-5 max-w-md mx-auto">{td('review.supporting')}</p>
                  <button
                    onClick={() => { setShowReviewModal(true); setReviewError(false); }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl transition-colors"
                    data-testid="review-open-btn"
                  >
                    {td('review.btn')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  {reviewError && (
                    <p className="text-xs text-red-500 mt-3" data-testid="review-error">{td('review.error')}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!model && (
          <div className="text-center py-8 text-slate-400" data-testid="estimator-empty">
            <p className="text-sm">{td('emptyState')}</p>
          </div>
        )}

        {model && !results && (
          <div className="text-center py-6 text-slate-400" data-testid="estimator-waiting">
            <p className="text-sm">{td('waitingState')}</p>
          </div>
        )}
      </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
          submitting={reviewSubmitting}
          td={td}
        />
      )}
    </div>
  );
};

export default DeploymentEstimator;
